import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getUserCountry } from '../../../utils/locationUtils';
import { StyledTextField } from './StyledTextField';
import ThankYouModal from './ThankYouModal';
import './WaitlistModal.css';

const WaitlistModal = ({ isOpen, onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    firstName: '',
    surname: '',
    email: '',
  });
  const [showThankYou, setShowThankYou] = useState(false);
  const [submittedData, setSubmittedData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [hasSubmitted, setHasSubmitted] = useState(false);

  useEffect(() => {
    // Check if user has already submitted (persistent across sessions)
    const hasSubmitted = localStorage.getItem('waitlist-submitted');
    if (hasSubmitted === 'true') {
      setHasSubmitted(true);
    }
  }, []);

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();

    // Prevent multiple submissions
    if (isSubmitting || hasSubmitted) return;

    setIsSubmitting(true);

    try {
      // Detect user's country dynamically
      const userCountry = await getUserCountry();

      // Use environment variable or fallback to the live URL
      const apiUrl = process.env.REACT_APP_API_BASE_URL
        ? `${process.env.REACT_APP_API_BASE_URL}/save-intrested-international-user`
        : 'https://eeizhsuc10.execute-api.us-east-1.amazonaws.com/live/save-intrested-international-user';

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: formData.email,
          name: `${formData.firstName} ${formData.surname}`,
          firstName: formData.firstName,
          surname: formData.surname,
          country: userCountry || 'Unknown',
          source: 'international',
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Mark as submitted (persistent across sessions)
        localStorage.setItem('waitlist-submitted', 'true');
        setHasSubmitted(true);

        toast.success('Thank you! You have been added to our waitlist.', {
          position: 'top-right',
          autoClose: 5000,
        });
        // Store the submitted data before clearing the form
        setSubmittedData(formData);
        setFormData({ firstName: '', surname: '', email: '' });
        setShowThankYou(true);
      } else {
        toast.error(
          'There was an error submitting your information. Please try again.',
          {
            position: 'top-right',
            autoClose: 5000,
          }
        );
      }
    } catch (error) {
      toast.error(
        'There was a network error. Please check your connection and try again.',
        {
          position: 'top-right',
          autoClose: 5000,
        }
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({ firstName: '', surname: '', email: '' });
    setShowThankYou(false);
    onClose();
  };

  const handleThankYouClose = () => {
    // Call the parent's onSubmit function when closing the Thank You modal
    if (submittedData) {
      onSubmit(submittedData);
    }
    setShowThankYou(false);
    setSubmittedData(null);
    onClose();
  };

  if (!isOpen) return null;

  // Show Thank You modal if form was submitted
  if (showThankYou) {
    return (
      <ThankYouModal isOpen={showThankYou} onClose={handleThankYouClose} />
    );
  }

  return (
    <div className="waitlist-modal-overlay" onClick={handleClose}>
      <div className="waitlist-modal" onClick={e => e.stopPropagation()}>
        <button className="waitlist-modal-close" onClick={handleClose}>
          ×
        </button>

        <div className="waitlist-modal-content">
          <h2 className="waitlist-modal-title">
            WE'RE LAUNCHING SOON IN THE UAE!
            <br />
            BE ONE OF THE FIRST TO GET EXCLUSIVE ACCESS.
          </h2>

          <form onSubmit={handleSubmit} className="waitlist-form">
            <div className="form-row">
              <StyledTextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                required
                variant="outlined"
              />

              <StyledTextField
                fullWidth
                label="Surname"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                required
                variant="outlined"
              />
            </div>

            <StyledTextField
              fullWidth
              label="Email Address"
              name="email"
              type="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              variant="outlined"
            />

            <div className="form-actions">
              <button
                type="button"
                className="btn-cancel"
                onClick={handleClose}
              >
                CANCEL
              </button>
              <button
                type="submit"
                className="btn-submit"
                disabled={isSubmitting}
              >
                {isSubmitting ? 'SUBMITTING...' : 'SUBMIT'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default WaitlistModal;
